import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { Preview } from '@creatomate/preview';

interface UseCreatomatePreviewReturn {
  isReady: boolean;
  isPlaying: boolean;
  currentTime: number;
  error: string | null;
  play: () => void;
  pause: () => void;
  seekToScene: (index: number) => void;
  dispose: () => void;
}

type PreviewPayload = Record<string, any> | null | undefined;

export function useCreatomatePreview(
  containerRef: React.RefObject<HTMLDivElement>,
  previewPayload: PreviewPayload,
  publicToken?: string,
  /**
   * Remount key — bump this (e.g. to the current wizard step) when the
   * parent JSX position of <PreviewPlayerEl/> changes. React's reconciliation
   * will detach the old container div and mount a fresh one at the new
   * position, but the iframe created by this hook still points at the *old*
   * (now-orphan) div. Changing `remountKey` forces dispose + re-init against
   * the current containerRef.current.
   */
  remountKey?: string | number
): UseCreatomatePreviewReturn {
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const previewRef = useRef<Preview | null>(null);

  // Derive scene start times from preview_payload.elements[].
  // Image elements named "Slide-N-Image" carry `time` (start) + `duration`.
  const sceneStartTimes = useMemo(() => {
    const elements: any[] = Array.isArray(previewPayload?.elements)
      ? previewPayload!.elements
      : [];
    return elements
      .filter(
        (el) =>
          el?.type === 'image' &&
          typeof el?.name === 'string' &&
          /^Slide-\d+-Image$/.test(el.name)
      )
      .map((el) => ({
        index: Number(el.name.match(/^Slide-(\d+)-Image$/)?.[1] ?? 0) - 1,
        time: typeof el.time === 'number' ? el.time : 0,
      }))
      .sort((a, b) => a.index - b.index)
      .map((e) => e.time);
  }, [previewPayload]);

  // Stable content-hash key so an unchanged-payload ref doesn't recreate the Preview.
  const payloadKey = useMemo(
    () => (previewPayload ? JSON.stringify(previewPayload) : ''),
    [previewPayload]
  );

  // Lifecycle: create a fresh Preview iframe per unique payloadKey.
  //
  // We tried reusing a single Preview instance and calling setSource() on every
  // payload change, which is the SDK's documented update path. In practice, the
  // second setSource() never resolves and the embed iframe silently stops
  // rendering (black pane, timer advances, no onLoad/onLoadComplete). Dispose +
  // recreate on each content change is the reliable workaround until we can
  // reproduce the hang against Creatomate and file upstream. Cost: ~1s iframe
  // re-init per edit, acceptable for the wizard flow.
  useEffect(() => {
    if (!containerRef.current || !publicToken) {
      if (!publicToken) {
        setError('Preview requires Creatomate Growth plan');
      }
      return;
    }
    if (!previewPayload || !payloadKey) return;

    let disposed = false;
    let preview: Preview | null = null;

    try {
      preview = new Preview(containerRef.current, 'player', publicToken);

      preview.onReady = () => {
        if (disposed || !preview) return;
        setIsReady(true);
        setError(null);
        preview.setSource(previewPayload).catch(() => {
          if (!disposed) setError('Failed to update preview source');
        });
      };

      preview.onPlay = () => {
        if (!disposed) setIsPlaying(true);
      };

      preview.onPause = () => {
        if (!disposed) setIsPlaying(false);
      };

      preview.onTimeChange = (time: number) => {
        if (!disposed) setCurrentTime(time);
      };

      // onError may not exist on all SDK versions — use try/catch as fallback
      try {
        (preview as any).onError = () => {
          if (!disposed) setError('Preview playback error');
        };
      } catch {
        // SDK version doesn't support onError callback
      }

      previewRef.current = preview;
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to initialize Creatomate preview'
      );
    }

    return () => {
      disposed = true;
      if (previewRef.current) {
        previewRef.current.dispose();
        previewRef.current = null;
      }
      setIsReady(false);
      setIsPlaying(false);
      setCurrentTime(0);
    };
    // previewPayload intentionally omitted — payloadKey is its content hash,
    // re-running on ref changes alone would cause needless iframe teardown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, publicToken, payloadKey, remountKey]);

  const play = useCallback(() => {
    previewRef.current?.play();
  }, []);

  const pause = useCallback(() => {
    previewRef.current?.pause();
  }, []);

  const seekToScene = useCallback(
    (index: number) => {
      if (!previewRef.current || index < 0 || index >= sceneStartTimes.length)
        return;
      const time = sceneStartTimes[index];
      previewRef.current.setTime(time);
    },
    [sceneStartTimes]
  );

  const dispose = useCallback(() => {
    if (previewRef.current) {
      previewRef.current.dispose();
      previewRef.current = null;
      setIsReady(false);
      setIsPlaying(false);
    }
  }, []);

  return {
    isReady,
    isPlaying,
    currentTime,
    error,
    play,
    pause,
    seekToScene,
    dispose,
  };
}
